import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { Observable, OperatorFunction, pipe } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';

import { CustomHttpParamEncoder } from './custom-http-param-encode.class';
import { FeaturesHandlerService } from './handlers/features-handler.service';
import { LongTaskHandlerService } from './handlers/long-task-handler.service';
import { MockHandlerService } from './handlers/mock-handler.service';
import { NotifyHandlerService } from './handlers/notify-handler.service';
import { RefreshHandlerService } from './handlers/refresh-handler.service';
import { IHttpParam } from './http-base.interface';

import { IDocumentBase, IFuncResponse } from '../api5-service/api.interface';
import { PageInfoService } from '../page-info/page-info.service';

const OUTPUT_FORMAT_PARAM = { out: 'xjson' };

/** Header for 503 error output in json */
export const forJsonOutputHeaders = { 'ISP-Client': 'Web-interface' };

/**
 * Http service for ISPmanager/BILLmanager 5
 * with 503 error handler
 */
@Injectable({
  providedIn: 'root',
})
export class HttpBaseService {
  /** The host of the manager */
  private readonly mgrHost = this.pageInfoService.mgrHost;

  /** Default output format */
  private readonly outputFormatParam = OUTPUT_FORMAT_PARAM;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly notifyHandler: NotifyHandlerService,
    private readonly featuresHandler: FeaturesHandlerService,
    private readonly longTaskHandler: LongTaskHandlerService,
    private readonly refreshHandler: RefreshHandlerService,
    private readonly pageInfoService: PageInfoService,
    private readonly mockHandler: MockHandlerService,
  ) {}

  /**
   * Convert params to FormData for upload files
   *
   * @param params
   */
  private convertToFormData(params: Params): FormData {
    const formData = new FormData();
    Object.keys(params).forEach(key => {
      const value =
        (params[key] as any[] | FileList | string | { name: string }) || '';

      // check on filelist
      if (typeof value[0] === 'object') {
        if ((value as FileList).length > 1) {
          for (const file of value as FileList) {
            formData.append(`${key}[]`, file);
          }
        } else {
          formData.append(`${key}`, value[0]);
        }
      } else if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        typeof (value as any).name === 'string'
      ) {
        // что тут происходит!?
        formData.append(key, value as any, (value as any).name);
      } else {
        formData.append(key, value as string);
      }
    });
    return formData;
  }

  /**
   * Returns `doc` key value from request's response
   */
  private getDocumentFromResponse<K extends IDocumentBase>(): OperatorFunction<
    IFuncResponse<K>,
    K
  > {
    return pipe(map(meta => (meta.doc ? meta.doc : (meta as unknown as K))));
  }

  /**
   * Returns `doc` key value from request's response
   */
  private getDocumentFromEvent<K extends IDocumentBase>(): OperatorFunction<
    HttpEvent<IFuncResponse<K>>,
    HttpEvent<K>
  > {
    return pipe(
      filter(event =>
        [HttpEventType.UploadProgress, HttpEventType.Response].includes(
          event.type,
        ),
      ),
      map(event => {
        return event.type === HttpEventType.Response
          ? ({
              ...event,
              body: event.body?.doc,
            } as HttpResponse<K>)
          : event;
      }),
    );
  }

  /**
   * Convert to HttpParams with encode
   *
   * @param params - request params
   */
  private convertParamsToHttpParams(params: IHttpParam): HttpParams {
    let httpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });
    Object.keys(params).forEach(key => {
      httpParams = httpParams.set(key, params[key]);
    });
    return httpParams;
  }

  private addStartHandlers<T>(request: Observable<T>): Observable<T> {
    return new Observable<T>(observer => {
      this.featuresHandler.onStart();

      const subscribeToRequest = request.subscribe(observer);

      return () => subscribeToRequest.unsubscribe();
    });
  }

  /**
   * Get request
   *
   * @param params - request params
   * @param headers - object with headers
   */
  get<
    R extends IDocumentBase = IDocumentBase,
    D extends IHttpParam = IHttpParam,
  >(params: D, headers?: HttpHeaders): Observable<R> {
    const requestFunction = this.httpClient.get(this.mgrHost, {
      params: this.convertParamsToHttpParams({
        ...this.outputFormatParam,
        ...params,
      }),
      headers: { ...forJsonOutputHeaders, ...headers },
    });

    return this.addStartHandlers(requestFunction).pipe(
      this.longTaskHandler.handle(),
      this.getDocumentFromResponse(),
      this.mockHandler.handle(),
      this.refreshHandler.handle(),
      this.featuresHandler.handle(),
      this.notifyHandler.handle(),
      share(),
    ) as Observable<R>;
  }

  /**
   * Post request
   *
   * @param params - request params
   * @param action - api handler path
   * @param headers - object with headers
   */
  post<R extends IDocumentBase = IDocumentBase, D extends Params = Params>(
    params: D,
    action?: string,
    headers?: HttpHeaders,
  ): Observable<R> {
    const requestFunction = this.httpClient.post(
      action || this.mgrHost,
      this.convertParamsToHttpParams({
        ...this.outputFormatParam,
        ...params,
      }),
      { headers: { ...forJsonOutputHeaders, ...headers } },
    );

    return this.addStartHandlers(requestFunction).pipe(
      this.longTaskHandler.handle(),
      this.getDocumentFromResponse(),
      this.mockHandler.handle(),
      this.refreshHandler.handle(),
      this.featuresHandler.handle(),
      this.notifyHandler.handle(),
      share(),
    ) as Observable<R>;
  }

  /**
   * Post request with observe. Usually used for uploading media files to server
   *
   * @param params - request params
   * @param action - api handler path
   */
  uploadFile<R extends IDocumentBase = IDocumentBase>(
    params: Params,
    action?: string,
  ): Observable<HttpEvent<R>> {
    const requestFunction = this.httpClient.post(
      action || this.mgrHost,
      this.convertToFormData({ ...params, ...this.outputFormatParam }),
      {
        reportProgress: true,
        observe: 'events',
        headers: { ...forJsonOutputHeaders, 'ngsw-bypass': '' },
      },
    );

    return this.addStartHandlers(requestFunction).pipe(
      this.longTaskHandler.handleEvent(),
      this.getDocumentFromEvent(),
      this.mockHandler.handleEvent(),
      this.refreshHandler.handleEvent(),
      this.featuresHandler.handleEvent(),
      this.notifyHandler.handleEvent(),
      share(),
    ) as Observable<HttpEvent<R>>;
  }
}
